import type { StorageAdapter } from 'ProdantixSdk/core/types';

export interface CookieJar {
  cookie: string;
}

export interface CookieStorageOptions {
  domain?: string;
  hostname?: string;
  jar?: CookieJar;
  name: string;
  random?: () => string;
  secure?: boolean;
}

const MAX_AGE_SECONDS = 31_536_000;
const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;
const DOMAIN_SHAPE = /^[a-z0-9.-]+$/i;

function defaultRandom(): string {
  return Math.random().toString(36).slice(2, 12);
}

function readAll(jar: CookieJar, name: string): string[] {
  const prefix = `${name}=`;
  const values: string[] = [];
  for (const part of jar.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      values.push(trimmed.slice(prefix.length));
    }
  }
  return values;
}

function attributes(domain: string | undefined, maxAge: number, secure: boolean): string {
  const parts = ['Path=/', `Max-Age=${maxAge}`, 'SameSite=Lax'];
  if (domain) {
    parts.push(`Domain=${domain}`);
  }
  if (secure) {
    parts.push('Secure');
  }
  return parts.join('; ');
}

function probe(jar: CookieJar, domain: string | undefined, secure: boolean, random: () => string): boolean {
  try {
    const name = `pdx_probe_${random()}`;
    const value = random();
    jar.cookie = `${name}=${value}; ${attributes(domain, 60, secure)}`;
    const accepted = readAll(jar, name).includes(value);
    jar.cookie = `${name}=; ${attributes(domain, 0, secure)}`;
    const stillThere = readAll(jar, name).includes(value);
    return accepted && !stillThere;
  } catch {
    return false;
  }
}

export function domainCandidates(hostname: string): string[] {
  const host = hostname.replace(/^\[|\]$/g, '').toLowerCase();
  if (host.includes(':') || IPV4.test(host) || !host.includes('.')) {
    return [];
  }
  const labels = host.split('.');
  const candidates: string[] = [];
  for (let index = labels.length - 2; index >= 0; index -= 1) {
    candidates.push(labels.slice(index).join('.'));
  }
  return candidates;
}

export function resolveCookieDomain(hostname: string, jar: CookieJar, secure: boolean, random: () => string = defaultRandom): string | undefined {
  for (const candidate of domainCandidates(hostname)) {
    if (probe(jar, candidate, secure, random)) {
      return candidate;
    }
  }
  return undefined;
}

function parse(raw: string): Map<string, string> {
  const map = new Map<string, string>();
  const parsed: unknown = JSON.parse(decodeURIComponent(raw));
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return map;
  }
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof value === 'string') {
      map.set(key, value);
    }
  }
  return map;
}

export class CookieStorage implements StorageAdapter {
  constructor(
    private readonly jar: CookieJar,
    private readonly name: string,
    private readonly domain: string | undefined,
    private readonly secure: boolean,
  ) {}

  getItem(key: string): string | null {
    return this.read().get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    const map = this.read();
    map.set(key, value);
    this.write(map);
  }

  removeItem(key: string): void {
    const map = this.read();
    if (!map.delete(key)) {
      return;
    }
    this.write(map);
  }

  private read(): Map<string, string> {
    try {
      let values = readAll(this.jar, this.name);
      if (values.length > 1 && this.domain) {
        this.jar.cookie = `${this.name}=; ${attributes(undefined, 0, this.secure)}`;
        values = readAll(this.jar, this.name);
      }
      const raw = values[0];
      if (!raw) {
        return new Map();
      }
      return parse(raw);
    } catch {
      return new Map();
    }
  }

  private write(map: Map<string, string>): void {
    try {
      if (map.size === 0) {
        this.jar.cookie = `${this.name}=; ${attributes(this.domain, 0, this.secure)}`;
        return;
      }
      const value = encodeURIComponent(JSON.stringify(Object.fromEntries(map)));
      this.jar.cookie = `${this.name}=${value}; ${attributes(this.domain, MAX_AGE_SECONDS, this.secure)}`;
    } catch {
      return;
    }
  }
}

function globalJar(): CookieJar | undefined {
  return (globalThis as { document?: CookieJar }).document;
}

function globalHostname(): string {
  return (globalThis as { location?: { hostname?: string } }).location?.hostname ?? '';
}

function globalSecure(): boolean {
  return (globalThis as { location?: { protocol?: string } }).location?.protocol === 'https:';
}

function callerDomain(domain: string | undefined): string | undefined {
  if (domain === undefined) {
    return undefined;
  }
  const stripped = domain.startsWith('.') ? domain.slice(1) : domain;
  return DOMAIN_SHAPE.test(stripped) ? stripped.toLowerCase() : undefined;
}

export function createCookieStorage(options: CookieStorageOptions): StorageAdapter | null {
  const jar = 'jar' in options ? options.jar : globalJar();
  if (!jar) {
    return null;
  }
  const cookieEnabled = (globalThis as { navigator?: { cookieEnabled?: boolean } }).navigator?.cookieEnabled;
  if (cookieEnabled === false) {
    return null;
  }
  const random = options.random ?? defaultRandom;
  const secure = options.secure ?? globalSecure();
  const hostname = options.hostname ?? globalHostname();
  try {
    const domain = callerDomain(options.domain) ?? resolveCookieDomain(hostname, jar, secure, random);
    if (!probe(jar, domain, secure, random)) {
      return null;
    }
    return new CookieStorage(jar, options.name, domain, secure);
  } catch {
    return null;
  }
}
